import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Form, Input, InputNumber, Button, Space,
    Card, Spin, Typography, Divider, Row, Col, Select, Alert, Empty, Switch
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { getPropertyConfig, updateEmailConfig, sendMonthlyUsageReportTest } from '../apiCalls';
import PageLayout from './PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getStoredUser } from '../utils/safeLocalStorage';
import { useTranslation } from 'react-i18next';
const { TextArea } = Input;

const { Title, Text } = Typography;

const cardStyle = { marginBottom: 24 };
const quoteTemplateDefaults = {
    proposal: { subject: 'Your solar proposal is ready', body: 'Your solar proposal is ready to review. Please contact us if you would like to discuss any part of it.' },
    reminder: { subject: 'A reminder about your solar proposal', body: 'A quick reminder that your solar proposal is ready. We are available to answer any questions.' },
    survey: { subject: 'Your solar survey appointment', body: 'Your property survey has been scheduled. Please let us know if access arrangements change.' },
    installation: { subject: 'Your solar installation appointment', body: 'Your solar installation has been scheduled. We will contact you if any final preparations are needed.' },
};

const formatPeriodLabel = (isoDate) => {
    if (!isoDate) return null;
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString('en-GB', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    });
};

const EmailConfigManager = () => {
    const { t } = useTranslation();
    const { propertyID } = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [propertyContactEmail, setPropertyContactEmail] = useState('');
    const [lastTestReportMeta, setLastTestReportMeta] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const pageLayoutProps = usePageLayout();

    const notificationEmailValue = Form.useWatch('notificationEmail', form);
    const emailAddressValue = Form.useWatch('emailAddress', form);

    const normalize = (value) => (typeof value === 'string' ? value.trim() : '');
    const notificationRecipient = normalize(notificationEmailValue);
    const contactRecipient = normalize(propertyContactEmail);
    const senderRecipient = normalize(emailAddressValue);

    const resolvedRecipient = notificationRecipient || contactRecipient || senderRecipient || '';
    const resolvedRecipientSource = notificationRecipient
        ? 'emailConfig.notificationEmail'
        : contactRecipient
            ? 'details.contact.email'
            : senderRecipient
                ? 'emailConfig.emailAddress'
                : null;

    useEffect(() => {
        setUser(getStoredUser());
    }, []);

    const fetchData = useCallback(async () => {
        setFetchLoading(true);
        try {
            const config = await getPropertyConfig(propertyID);
            const emailConfig = config?.emailConfig || {};
            const quoteAutomation = emailConfig.quoteAutomation || {};
            setPropertyContactEmail(config?.details?.contact?.email || '');

            form.setFieldsValue({
                smtpHost: emailConfig.smtpHost || 'smtp.gmail.com',
                smtpPort: emailConfig.smtpPort || 587,
                smtpSecure: emailConfig.smtpSecure !== undefined ? emailConfig.smtpSecure : false,
                notificationEmail: emailConfig.notificationEmail || '',
                monthlyReportTone: emailConfig.monthlyReportTone || 'adaptive',
                emailAddress: emailConfig.emailAddress || '',
                emailPassword: emailConfig.emailPassword || '',
                senderName: emailConfig.senderName || '',
                emailSubject: emailConfig.emailSubject || '',
                emailBody: emailConfig.emailBody || '',
                quoteAutomation: {
                    replyTo: quoteAutomation.replyTo || '',
                    remindersEnabled: quoteAutomation.remindersEnabled !== false,
                    reminderHoursAfterDue: quoteAutomation.reminderHoursAfterDue || 0,
                    templates: Object.fromEntries(Object.entries(quoteTemplateDefaults).map(([type, fallback]) => [type, {
                        subject: quoteAutomation.templates?.[type]?.subject || fallback.subject,
                        body: quoteAutomation.templates?.[type]?.body || fallback.body,
                    }])),
                },
                testRecipient: '',
            });
        } catch (err) {
            enqueueSnackbar(t('email.error.loading'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setFetchLoading(false);
        }
    }, [propertyID, form, enqueueSnackbar, t]);

    useEffect(() => {
        if (propertyID) {
            fetchData();
        }
    }, [propertyID, fetchData]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const payload = {
                smtpHost: values.smtpHost,
                smtpPort: values.smtpPort,
                smtpSecure: values.smtpSecure,
                notificationEmail: values.notificationEmail,
                monthlyReportTone: values.monthlyReportTone,
                emailAddress: values.emailAddress,
                emailPassword: values.emailPassword,
                senderName: values.senderName,
                emailSubject: values.emailSubject,
                emailBody: values.emailBody,
                quoteAutomation: values.quoteAutomation,
            };

            await updateEmailConfig(propertyID, payload);
            enqueueSnackbar(t('email.success'), { variant: 'success' });
        } catch (err) {
            const errorMsg = err.response?.data || t('email.error.saving');
            enqueueSnackbar(errorMsg, { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSendTestReport = async () => {
        setTestLoading(true);
        setLastTestReportMeta(null);
        try {
            const testRecipient = form.getFieldValue('testRecipient');
            const response = await sendMonthlyUsageReportTest({
                propertyID,
                testRecipient,
            });

            const sourceLabel = response?.dataSource === 'legacy-event-fallback'
                ? 'Legacy event fallback'
                : 'Daily aggregate';

            setLastTestReportMeta({
                dataSource: response?.dataSource || 'daily-aggregate',
                totalInteractions: response?.totalInteractions ?? 0,
                periodStart: response?.periodStart,
                periodEnd: response?.periodEnd,
            });

            enqueueSnackbar(
                `Test report sent to ${response?.recipient || testRecipient || 'configured recipient'} (${sourceLabel}, interactions: ${response?.totalInteractions ?? 0})`,
                { variant: 'success' }
            );
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.response?.data || 'Failed to send test monthly report';
            enqueueSnackbar(errorMsg, { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setTestLoading(false);
        }
    };

    const canSendTestReport = user?.role === 'superAdmin' || user?.role === 'super-admin';
    const testReportMonthLabel = formatPeriodLabel(lastTestReportMeta?.periodStart);

    // Check if user has permission (superAdmin, super-admin, or admin)
    if (user && user.role !== 'superAdmin' && user.role !== 'super-admin' && user.role !== 'admin') {
        return (
            <PageLayout {...pageLayoutProps}>
                <Empty
                    description={t('email.accessDenied')}
                    style={{ marginTop: 100 }}
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: 8 }}>{t('email.title')}</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                    {t('email.subtitle')}
                </Text>

                <Alert
                    message={t('email.securityNotice')}
                    description={t('email.securityDesc')}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 32 }}
                    icon={<LockOutlined />}
                />

                <Spin spinning={fetchLoading}>
                    <Form form={form} onFinish={handleSubmit} layout="vertical">

                        <Card title="Monthly Report Recipient Status" style={cardStyle}>
                            <Alert
                                type={resolvedRecipient ? 'success' : 'warning'}
                                showIcon
                                message={resolvedRecipient
                                    ? `Reports will be sent to: ${resolvedRecipient}`
                                    : 'No monthly report recipient is configured yet.'}
                                description={resolvedRecipientSource
                                    ? `Resolved from ${resolvedRecipientSource}`
                                    : 'Set Monthly report recipient or contact email to enable sends.'}
                            />
                        </Card>

                        {/* SMTP Server Settings */}
                        <Card title={t('email.smtp')} style={cardStyle}>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={t('email.host')}
                                        name="smtpHost"
                                        rules={[{ required: true, message: t('email.validation.host') }]}
                                    >
                                        <Input
                                            placeholder={t('email.hostPlaceholder')}
                                            prefix={<LockOutlined />}
                                            maxLength={253}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={t('email.port')}
                                        name="smtpPort"
                                        rules={[{ required: true, message: t('email.validation.port') }]}
                                    >
                                        <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="587" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={t('email.secure')}
                                        name="smtpSecure"
                                        rules={[{ required: true, message: t('email.validation.secure') }]}
                                    >
                                        <Select placeholder="Select connection type">
                                            <Select.Option value={false}>{t('email.noTls')}</Select.Option>
                                            <Select.Option value={true}>{t('email.yesSsl')}</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Email Credentials */}
                        <Card title={t('email.credentials')} style={cardStyle}>
                            <Form.Item
                                label="Monthly report recipient"
                                name="notificationEmail"
                                rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                            >
                                <Input
                                    placeholder="reports@yourcompany.com"
                                    type="email"
                                    maxLength={254}
                                />
                            </Form.Item>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                Optional dedicated inbox for monthly usage reports.
                            </Text>

                            <Form.Item
                                label="Monthly report tone"
                                name="monthlyReportTone"
                                rules={[{ required: true, message: 'Please select a report tone' }]}
                            >
                                <Select>
                                    <Select.Option value="adaptive">Adaptive (recommended)</Select.Option>
                                    <Select.Option value="conservative">Conservative</Select.Option>
                                    <Select.Option value="growth">Growth-focused</Select.Option>
                                    <Select.Option value="concise">Data-analyst concise</Select.Option>
                                </Select>
                            </Form.Item>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                Controls wording for the monthly usage email while keeping the same KPI layout.
                            </Text>

                            <Form.Item
                                label={t('email.emailAddress')}
                                name="emailAddress"
                                rules={[
                                    { required: true, message: t('email.validation.emailAddress') },
                                    { type: 'email', message: t('email.validation.emailAddressInvalid') }
                                ]}
                            >
                                <Input
                                    placeholder={t('email.emailPlaceholder')}
                                    type="email"
                                    maxLength={254}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('email.emailPassword')}
                                name="emailPassword"
                                rules={[{ required: true, message: t('email.validation.emailPassword') }]}
                            >
                                <Input.Password
                                    placeholder={t('email.passwordPlaceholder')}
                                />
                            </Form.Item>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                <strong>For Gmail:</strong> {t('email.gmailHelp')} <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer">{t('email.learnMore')}</a>
                            </Text>
                        </Card>

                        {canSendTestReport && (
                            <Card title="Monthly Report Test Send" style={cardStyle}>
                                <Alert
                                    style={{ marginBottom: 16 }}
                                    type="info"
                                    showIcon
                                    message="Period used for monthly reports"
                                    description="Monthly reports always use the previous calendar month (UTC), not the current rolling 30-day dashboard range."
                                />
                                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                    Send a one-off test monthly report email for this property. If left blank, the configured recipient chain is used.
                                </Text>
                                <Row gutter={24} align="bottom">
                                    <Col xs={24} sm={14}>
                                        <Form.Item
                                            label="Test recipient override"
                                            name="testRecipient"
                                            rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                                        >
                                            <Input placeholder="qa@example.com" type="email" maxLength={254} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={10}>
                                        <Button
                                            type="default"
                                            loading={testLoading}
                                            onClick={handleSendTestReport}
                                            style={{ width: '100%' }}
                                        >
                                            {testLoading ? 'Sending test report...' : 'Send test monthly report'}
                                        </Button>
                                    </Col>
                                </Row>
                                {lastTestReportMeta && (
                                    <Alert
                                        style={{ marginTop: 12 }}
                                        type={lastTestReportMeta.dataSource === 'legacy-event-fallback' ? 'warning' : 'success'}
                                        showIcon
                                        message={`Data source: ${lastTestReportMeta.dataSource === 'legacy-event-fallback' ? 'Legacy event fallback' : 'Daily aggregate'}`}
                                        description={`Interactions in period: ${lastTestReportMeta.totalInteractions}. Period: ${lastTestReportMeta.periodStart || 'n/a'} to ${lastTestReportMeta.periodEnd || 'n/a'}${testReportMonthLabel ? ` (previous month: ${testReportMonthLabel})` : ''}.`}
                                    />
                                )}
                            </Card>
                        )}

                        {/* Email Sender */}
                        <Card title={t('email.sender')} style={cardStyle}>
                            <Form.Item
                                label={t('email.senderName')}
                                name="senderName"
                                rules={[{ required: true, message: t('email.validation.senderName') }]}
                            >
                                <Input
                                    placeholder={t('email.subjectPlaceholder')}
                                    maxLength={100}
                                />
                            </Form.Item>
                            <Text type="secondary" style={{ display: 'block' }}>
                                {t('email.senderNameHelp')}
                            </Text>
                        </Card>

                        {/* Email Template */}
                        <Card title={t('email.template')} style={cardStyle}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                {t('email.templateHelp')}
                                Available placeholders: <code>{'{{firstName}}'}</code>, <code>{'{{lastName}}'}</code>, <code>{'{{companyName}}'}</code>
                            </Text>
                            <Form.Item label={t('email.subject')} name="emailSubject">
                                <Input placeholder="e.g. Your {{companyName}} Solar Quotation" maxLength={100} />
                            </Form.Item>
                            <Form.Item label={t('email.body')} name="emailBody">
                                <TextArea
                                    rows={8}
                                    placeholder={`Dear {{firstName}} {{lastName}},\n\nPlease find attached your {{companyName}} quotation.\n\nBest regards,\n{{companyName}} Team`}
                                    maxLength={5000}
                                />
                            </Form.Item>
                        </Card>

                        <Card title="Quote Communication Automation" style={cardStyle}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                Templates support <code>{'{{customerName}}'}</code> and <code>{'{{companyName}}'}</code>. Automated reminders are deduplicated until the follow-up due date changes.
                            </Text>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Reply-to address" name={['quoteAutomation', 'replyTo']} rules={[{ type: 'email', message: 'Please enter a valid email address' }]}>
                                        <Input type="email" placeholder="sales@yourcompany.com" maxLength={254} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={6}>
                                    <Form.Item label="Automated reminders" name={['quoteAutomation', 'remindersEnabled']} valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={6}>
                                    <Form.Item label="Hours after due" name={['quoteAutomation', 'reminderHoursAfterDue']}>
                                        <InputNumber min={0} max={720} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {Object.keys(quoteTemplateDefaults).map((type) => (
                                <Card key={type} size="small" title={`${type[0].toUpperCase()}${type.slice(1)} email`} style={{ marginBottom: 16 }}>
                                    <Form.Item label="Subject" name={['quoteAutomation', 'templates', type, 'subject']} rules={[{ required: true }]}>
                                        <Input maxLength={200} />
                                    </Form.Item>
                                    <Form.Item label="Message" name={['quoteAutomation', 'templates', type, 'body']} rules={[{ required: true }]}>
                                        <TextArea rows={4} maxLength={10000} />
                                    </Form.Item>
                                </Card>
                            ))}
                        </Card>

                        <Divider />

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                    style={{ minWidth: 200 }}
                                >
                                    {loading ? t('email.saving') : t('email.save')}
                                </Button>
                            </Space>
                        </Form.Item>

                    </Form>
                </Spin>
            </div>
        </PageLayout>
    );
};

export default EmailConfigManager;
